import React, { useRef, useState } from "react";
import { Field } from "formik";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import MuiSelect from "@mui/material/Select";
import ListSubheader from "@mui/material/ListSubheader";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";

const optionValue = option => (Array.isArray(option) ? option[0] : option?.value);

const Select = ({
  options = [],
  label,
  InputLabelProps,
  size,
  required,
  subheaderFontSize = "0.875rem",
  showNAButton = true,
  openOnFocus = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const skipFocusOpenRef = useRef(false);

  const closeMenu = ({ fromSelect = false } = {}) => {
    if (!openOnFocus) return;
    if (fromSelect) skipFocusOpenRef.current = true;
    setIsMenuOpen(false);
  };

  return (
    <FormControl fullWidth variant="outlined" size={size} required={required}>
      <Field {...props}>
        {({ field, form, meta }) => {
          const error = (meta.touched || form.submitCount > 0) && meta.error;
          const isEmpty = !field.value || field.value === "";
          const showNABtn = showNAButton && isFocused && isEmpty;
          const hasCurrentValue = options.some(option => optionValue(option) === field.value);
          const resolvedOptions =
            field.value && !hasCurrentValue ? [[field.value, field.value], ...options] : options;

          const handleNAClick = () => {
            form.setFieldValue(field.name, "N/A");
            closeMenu({ fromSelect: true });
          };

          return (
            <>
              <InputLabel id={`select-label-${field.name}`} error={!!error}>
                {label}
              </InputLabel>
              <MuiSelect
                {...props}
                data-id={field.name}
                labelId={`select-label-${field.name}`}
                label={label}
                id={field.name}
                value={field.value || ""}
                error={!!error}
                disabled={props.disabled}
                open={openOnFocus ? isMenuOpen : undefined}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                onChange={event => {
                  form.setFieldValue(field.name, event.target.value);
                  closeMenu({ fromSelect: true });
                }}
                onFocus={event => {
                  setIsFocused(true);
                  if (openOnFocus) {
                    if (skipFocusOpenRef.current) {
                      skipFocusOpenRef.current = false;
                    } else {
                      setIsMenuOpen(true);
                    }
                  }
                  props.onFocus?.(event);
                }}
                onBlur={event => {
                  setIsFocused(false);
                  props.onBlur?.(event);
                }}
                onOpen={event => {
                  if (openOnFocus) setIsMenuOpen(true);
                  props.onOpen?.(event);
                }}
                onClose={event => {
                  closeMenu();
                  props.onClose?.(event);
                }}
                endAdornment={
                  showNABtn ? (
                    <InputAdornment position="end" sx={{ mr: 3 }}>
                      <Chip
                        label="N/A"
                        size="small"
                        clickable
                        color="primary"
                        variant="outlined"
                        onMouseDown={handleNAClick}
                        sx={{ cursor: "pointer" }}
                      />
                    </InputAdornment>
                  ) : null
                }
              >
                {resolvedOptions.map((option, idx) => {
                  if (Array.isArray(option)) {
                    const [value, label] = option;
                    return (
                      <MenuItem key={value} value={value} data-id="select-item">
                        {label}
                      </MenuItem>
                    );
                  }

                  const { value, label, disabled = false, subheader = false } = option;

                  if (subheader) {
                    return (
                      <ListSubheader
                        key={`subheader-${idx}`}
                        style={{ fontSize: subheaderFontSize }}
                      >
                        {label}
                      </ListSubheader>
                    );
                  }

                  return (
                    <MenuItem key={value} value={value} disabled={disabled} data-id="select-item">
                      {label}
                    </MenuItem>
                  );
                })}
              </MuiSelect>

              {error && (
                <FormHelperText htmlFor={field.name} error={!!error}>
                  {error}
                </FormHelperText>
              )}
            </>
          );
        }}
      </Field>
    </FormControl>
  );
};

export default Select;
